/**
 * Billing-country resolution for the checkout VAT summary (Task 4, fix round 1).
 *
 * Review finding: the summary used `state.vatEstimate` — the shop-entry IP-estimated lookup
 * (Task 2, fetched once in `shopping.tsx` before the member ever enters an address) — for the VAT
 * rate shown right above the Pay button. A member whose billing country differs from where their
 * IP resolved to (VPN, travel, billing for a company registered elsewhere, …) saw the WRONG rate.
 *
 * `resolveBillingCountryId` picks the country id that will actually be billed, matching the same
 * precedence `paymentinfo.tsx` already uses for payment-method eligibility
 * (`getAllowedPaymentMethod`): the member's own account country when billing defaults to them
 * ("Use my info"), or the selected separate billing address's country when one is chosen instead.
 * It never falls back to the account country once a separate address is in play — if nothing is
 * selected yet, the billing country is simply unknown.
 *
 * `resolveDisplayVatRate` then prefers the per-billing-country estimate over the IP one the
 * instant a billing country is known — even while that estimate is still in flight (shows 0, not
 * the stale/wrong IP rate) — and only falls back to the IP estimate while no billing country has
 * been resolved at all.
 */

export interface ResolveBillingCountryIdInput {
  curUserToBilling: boolean
  curUserInfoCountryId: number | null | undefined
  billingAddresses: Array<{useStatus?: boolean; countryId?: number | null}> | null | undefined
}

export function resolveBillingCountryId({
  curUserToBilling,
  curUserInfoCountryId,
  billingAddresses,
}: ResolveBillingCountryIdInput): number | null {
  if (curUserToBilling) {
    return curUserInfoCountryId || null
  }
  const selected = billingAddresses?.find((item) => item?.useStatus === true)
  return selected?.countryId || null
}

export interface ResolveDisplayVatRateInput {
  billingCountryId: number | null
  billingEstimate: {rate: number} | null | undefined
  ipEstimate: {rate: number} | null | undefined
}

export function resolveDisplayVatRate({
  billingCountryId,
  billingEstimate,
  ipEstimate,
}: ResolveDisplayVatRateInput): number {
  if (billingCountryId) {
    return billingEstimate?.rate ?? 0
  }
  return ipEstimate?.rate ?? 0
}
